/**
 * @file Components/Admin/AdminLayout.jsx
 * @description Admin panel layout wrapper with sidebar navigation,
 * header, and content area using React Router Outlet for nested routes.
 */

import React, { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Package, 
  Users, 
  MessageSquare, 
  Calendar, 
  Settings,
  Menu,
  X,
  UserCog,
  FileText,
  Inbox,
  Globe
} from 'lucide-react';

const AdminLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const location = useLocation();

  const menuItems = [
    { path: '/admin/dashboard', icon: <LayoutDashboard size={20} />, label: 'Dashboard' },
    { path: '/admin/tours', icon: <Package size={20} />, label: 'Tours' },
    { path: '/admin/users', icon: <Users size={20} />, label: 'Users' },
    { path: '/admin/user-management', icon: <UserCog size={20} />, label: 'User Management' },
    { path: '/admin/testimonials', icon: <MessageSquare size={20} />, label: 'Testimonials' },
    { path: '/admin/blog', icon: <FileText size={20} />, label: 'Blog' },
    { path: '/admin/queries', icon: <Inbox size={20} />, label: 'Queries' },
    { path: '/admin/bookings', icon: <Calendar size={20} />, label: 'Bookings' },
    { path: '/admin/site-content', icon: <Globe size={20} />, label: 'Site Content' },
    { path: '/admin/settings', icon: <Settings size={20} />, label: 'Settings' },
  ];

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div 
        className={`${
          isSidebarOpen ? 'w-64' : 'w-20'
        } bg-indigo-800 text-white transition-all duration-300 ease-in-out fixed h-full`}
      >
        <div className="flex justify-between items-center p-4">
          <h2 className={`${!isSidebarOpen && 'hidden'} font-bold text-xl`}>Admin Panel</h2>
          <button 
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="p-2 hover:bg-indigo-700 rounded-lg"
          >
            {isSidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
        <nav className="mt-8">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center px-4 py-3 ${
                location.pathname === item.path || location.pathname.startsWith(item.path + '/')
                  ? 'bg-indigo-700 border-r-4 border-white'
                  : 'hover:bg-indigo-700'
              } transition-colors duration-200`}
            >
              <span className="inline-flex">{item.icon}</span>
              <span className={`${!isSidebarOpen && 'hidden'} ml-4`}>
                {item.label}
              </span>
            </Link>
          ))}
        </nav>
      </div>

      {/* Main Content */}
      <div className={`flex-1 ${isSidebarOpen ? 'ml-64' : 'ml-20'} transition-all duration-300 ease-in-out`}>
        <header className="bg-white shadow-md">
          <div className="mx-auto px-4 py-6">
            <h1 className="text-2xl font-semibold text-gray-900">
              {menuItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
            </h1>
          </div>
        </header>
        <main className="mx-auto px-4 py-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout; 